import React, { useEffect, useMemo, useRef } from 'react';
import { Bot, Mic, User } from 'lucide-react';
import { AiInterviewTranscriptLine, InterviewStage, TranscriptSpeaker } from '../../types';

interface Props {
  lines: AiInterviewTranscriptLine[];
  live?: boolean;
  aiSpeaking?: boolean;
  micOn?: boolean;
  stage?: InterviewStage;
}

const ParticipantStatus: React.FC<{ aiSpeaking: boolean; micOn: boolean; stage: InterviewStage }> = ({
  aiSpeaking,
  micOn,
  stage,
}) => {
  const listening = micOn && stage === 'LISTENING';
  return (
    <div className="flex items-center gap-3 px-4 pt-4 pb-2.5 border-b border-zinc-100 bg-white rounded-t-card">
      <div className="relative shrink-0">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
            aiSpeaking ? 'bg-brand-600' : 'bg-brand-600/70'
          }`}
        >
          <Bot className="w-5 h-5" />
        </div>
        {aiSpeaking && (
          <span
            aria-hidden
            className="absolute -inset-1.5 rounded-full border-2 border-brand-400 animate-ping opacity-60"
          />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">AI Interviewer</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          {aiSpeaking ? (
            <>
              <span className="flex items-end gap-0.5 h-3" aria-hidden>
                <span className="w-0.5 h-2 bg-brand-500 rounded-full animate-pulse" />
                <span className="w-0.5 h-3 bg-brand-500 rounded-full animate-pulse [animation-delay:150ms]" />
                <span className="w-0.5 h-2.5 bg-brand-500 rounded-full animate-pulse [animation-delay:300ms]" />
              </span>
              <p className="text-xs text-brand-600">Interviewer is speaking…</p>
            </>
          ) : listening ? (
            <>
              <Mic className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-xs text-emerald-700">Listening — your turn to answer</p>
            </>
          ) : (
            <p className="text-xs text-zinc-500">The interviewer will speak in turn</p>
          )}
        </div>
      </div>
    </div>
  );
};

interface Turn {
  speaker: TranscriptSpeaker;
  text: string;
  timestamp?: string;
}

const groupIntoTraces = (lines: AiInterviewTranscriptLine[]): Turn[] => {
  const turns: Turn[] = [];
  for (const line of lines) {
    const last = turns[turns.length - 1];
    if (last && last.speaker === line.speaker) {
      last.text = last.text ? `${last.text}\n${line.text}` : line.text;
    } else {
      turns.push({ speaker: line.speaker, text: line.text, timestamp: line.timestamp ?? undefined });
    }
  }
  return turns;
};

const ConversationLog: React.FC<Props> = ({ lines, live, aiSpeaking = false, micOn = false, stage = 'IDLE' }) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const turns = useMemo(() => groupIntoTraces(lines), [lines]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [turns.length]);

  return (
    <div className="flex-1 min-h-0 rounded-card border border-zinc-200 bg-page flex flex-col overflow-hidden">
      {live && <ParticipantStatus aiSpeaking={aiSpeaking} micOn={micOn} stage={stage} />}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {turns.length === 0 && (
          <p className="text-xs text-zinc-400 text-center py-8">
            {live ? 'The conversation will appear here.' : 'No transcript yet.'}
          </p>
        )}
        {turns.map((turn, index) => {
          const isAi = turn.speaker === ('AI' as TranscriptSpeaker);
          return (
            <div
              key={`${index}-${turn.timestamp ?? ''}`}
              className={`flex flex-col gap-1 ${isAi ? 'items-start' : 'items-end'}`}
            >
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide ${
                  isAi ? 'text-zinc-400' : 'text-brand-500'
                }`}
              >
                {isAi ? 'AI Interviewer' : 'Candidate'}
              </span>
              <div className={`flex gap-2.5 max-w-[78%] ${isAi ? '' : 'flex-row-reverse'}`}>
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    isAi ? 'bg-brand-50 text-brand-500' : 'bg-zinc-100 text-zinc-500'
                  }`}
                >
                  {isAi ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <div
                  className={`min-w-0 max-w-full rounded-card px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    isAi ? 'bg-white border border-zinc-200 text-foreground' : 'bg-brand-500 text-white'
                  }`}
                >
                  {turn.text}
                  {turn.timestamp && (
                    <p className={`mt-1 text-[10px] ${isAi ? 'text-zinc-400' : 'text-brand-100'}`}>
                      {new Date(turn.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default ConversationLog;